import { createContext, useState, useContext, useMemo } from 'react'
import { baseURL } from '../../../../services/api'

export const MarkupContext = createContext()

export const MarkupProvider = ({ children }) => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingDashboard, setLoadingDashboard] = useState(false)
  const [dashboard, setDashboard] = useState({
    totalPedidos: 0,
    vendaMes: 0,
  })

  const [showGraphics, setShowGraphics] = useState(false)

  function handleShowGraphics() {
    setShowGraphics(!showGraphics)
  }

  const defaultValueMes = (new Date().getMonth() + 1)
    .toString()
    .padStart(2, '0')
  const defaultValueAno = new Date().getFullYear().toString()

  async function getInfoMarkup(data) {
    setLoading(true)
    setShowGraphics(false)
    const params = {
      numero_pedido: data.numero_pedido,
      data_entrada: data.data_entrada,
      data_saida: data.data_saida,
      ano: data.ano,
      mes: data.mes,
    }

    const response = await baseURL.post(
      '/markup/lista-informacoes-markup',
      params
    )

    const dataNotNunnotaDuplicate = response.data.filter(
      (item, index, self) => {
        return index === self.findIndex((t) => t.Nunota === item.Nunota)
      }
    )

    setData(dataNotNunnotaDuplicate)

    setLoading(false)

    getPedidosDashboard(data)
  }

  async function getPedidosDashboard(data) {
    setLoadingDashboard(true)
    const response = await baseURL.get(
      `/pedido/lista-simulacao-mes/${data.ano ? data.ano : defaultValueAno}${
        data.mes
      }`
    )

    setDashboard({
      totalPedidos: response.data[0]?.total_pedidos,
      vendaMes: response.data[0]?.vendas_mes,
    })

    setLoadingDashboard(false)
  }

  const custoTotalSummary = data?.reduce((acc, item) => {
    const { produtos } = item

    const custoTotal = produtos?.reduce((acc, item) => {
      const { lucro_bruto, valor_total_preco_base } = item

      const lucroBruto = parseFloat(lucro_bruto) || 0
      const valorTotalPrecoBase = parseFloat(valor_total_preco_base) || 0

      const valorTotalPrecoBaseMenosLucroBruto =
        valorTotalPrecoBase - lucroBruto

      return acc + valorTotalPrecoBaseMenosLucroBruto
    }, 0)

    return acc + custoTotal
  }, 0)

  const summary = useMemo(() => {
    if (!data) {
      return {
        jurosPagamentoAtrasado: 0,
        lucroCondicaoPagamento: 0,
        lucroSobreFrete: 0,
        sobraInnovation: 0,
        lucroBruto: 0,
        custoTotal: 0,
      }
    }

    return data.reduce(
      (acc, curr) => {
        const {
          juros_pagamento_atrasado,
          lucro_condicao_pagamento,
          lucro_sobre_frete,
          sobra_innovation,
          balanco_lucro,
          VlrCustoTal,
        } = curr

        const custoTotal = parseFloat(VlrCustoTal) || 0
        const jurosPagamentoAtrasado = parseFloat(juros_pagamento_atrasado)
        const lucroCondicaoPagamento = parseFloat(lucro_condicao_pagamento)
        const lucroSobreFrete = parseFloat(lucro_sobre_frete)
        const sobraInnovation = parseFloat(sobra_innovation) || 0
        const lucroBruto = parseFloat(balanco_lucro)

        return {
          custoTotal: acc.custoTotal + custoTotal,
          jurosPagamentoAtrasado:
            acc.jurosPagamentoAtrasado + jurosPagamentoAtrasado,
          lucroCondicaoPagamento:
            acc.lucroCondicaoPagamento + lucroCondicaoPagamento,
          lucroSobreFrete: acc.lucroSobreFrete + lucroSobreFrete,
          sobraInnovation: acc.sobraInnovation + sobraInnovation,
          lucroBruto: acc.lucroBruto + lucroBruto,
        }
      },
      {
        jurosPagamentoAtrasado: 0,
        lucroCondicaoPagamento: 0,
        lucroSobreFrete: 0,
        sobraInnovation: 0,
        lucroBruto: 0,
        custoTotal: 0,
      }
    )
  }, [data])

  const calcIcmsSt = data?.reduce((acc, item) => {
    const { icms_st } = item

    const icmsSt = parseFloat(icms_st) || 0

    return acc + icmsSt
  }, 0)

  const summaryValorItems = useMemo(() => {
    if (!data) {
      return { precoBase: 0, precoVenda: 0, icmsSt: 0 }
    }

    return data.reduce(
      (acc, curr) => {
        const { produtos } = curr

        if (!produtos || produtos === null) {
          return acc
        }

        const soma = produtos.reduce(
          (
            subtotal,
            { valor_unit, quantidade_negociada, preco_base, icms_st }
          ) => {
            const valorVendaItens = parseFloat(preco_base) || 0
            const quantidadeNegociada = parseFloat(quantidade_negociada) || 0
            const precoVenda = parseFloat(valor_unit) || 0
            const icmsSt = parseFloat(icms_st) || 0

            return {
              precoBase:
                subtotal.precoBase + valorVendaItens * quantidadeNegociada,
              precoVenda:
                subtotal.precoVenda + precoVenda * quantidadeNegociada,
              icmsSt: subtotal.icmsSt + icmsSt,
            }
          },
          acc
        )

        return soma
      },
      { precoBase: 0, precoVenda: 0, icmsSt: 0 }
    )
  }, [data])

  // const emptyOrNullValues = data.filter((item) =>
  //   item.produtos.some(
  //     (produto) =>
  //       produto.valor_unit === null ||
  //       produto.valor_unit === undefined ||
  //       produto.valor_unit === '' ||
  //       produto.valor_unit === '0.0' ||
  //       produto.valor_unit === 0 ||
  //       produto.valor_unit === '0' ||
  //       produto.valor_unit === '0,0' ||
  //       produto.valor_unit === '0,00'
  //   )
  // )

  // console.log('IS EMPY', emptyOrNullValues)

  return (
    <MarkupContext.Provider
      value={{
        data,
        setData,
        getInfoMarkup,
        loading,
        setLoading,
        defaultValueMes,
        defaultValueAno,
        dashboard,
        loadingDashboard,
        handleShowGraphics,
        showGraphics,
        summaryValorItems,
        summary,
        calcIcmsSt,
        custoTotalSummary,
      }}
    >
      {children}
    </MarkupContext.Provider>
  )
}

export const useMarkup = () => useContext(MarkupContext)
